import type { Task } from "@app/core";
import { upcomingWithin } from "@app/core";
import { detectPlatform } from "../../platform.js";
import { formatHmDate } from "../../lib/format.js";
import { tr } from "../../lib/tr.js";
import { useSettingsStore } from "../../stores/settings.store.js";
import { notificationColor } from "../task-colors.js";
import { tauriNotifier } from "./adapters/tauri.js";
import { capacitorNotifier } from "./adapters/capacitor.js";
import { webNotifier } from "./adapters/web.js";

/** What a platform notifier emits. `color` is a hex accent (rendered as an icon
 *  swatch where supported); null when absent or when a bundle mixes colors. */
export interface NotificationPayload {
  title: string;
  body: string;
  color?: string | null;
  /** Task ids behind this fire — passed to the OS as `extra` so a notification
   *  action (Android "Snooze") can resolve back to the task(s). Virtual-occurrence
   *  ids carry the `::v::` suffix; the action handler splits them. */
  taskIds?: string[];
}

/** A reminder the OS should fire at `atMs` (epoch ms, minute-aligned). */
export interface ScheduledFire {
  atMs: number;
  payload: NotificationPayload;
}

/** Platform sink — emits one OS notification now. The scheduler owns all timing
 *  UNLESS the sink exposes `scheduleAll`: then the OS owns it (Android, where
 *  the WebView is suspended/killed in background and in-process timers die) —
 *  reconcile() hands over the whole desired fire set and arms no timers. */
export interface Notifier {
  send(payload: NotificationPayload): Promise<void>;
  /** Diff `fires` (sorted by atMs) against the OS's already-scheduled set:
   *  schedule new/changed, cancel no-longer-wanted, leave the rest untouched. */
  scheduleAll?(fires: ScheduledFire[]): Promise<void>;
  /** Fire a one-off diagnostic notification — immediately when `atMs` is omitted,
   *  otherwise at that instant. Unlike `send`, this THROWS on a permission denial
   *  or scheduling error so the Settings UI can report what went wrong. `background`
   *  is true when the OS will deliver it with the app closed (Android); false when
   *  it rides an in-process timer that needs the app to stay open (web/Tauri). */
  sendTest?(opts: { atMs?: number }): Promise<{ background: boolean }>;
}

function pickNotifier(): Notifier {
  switch (detectPlatform()) {
    case "tauri": return tauriNotifier;
    case "capacitor": return capacitorNotifier;
    default: return webNotifier;
  }
}

const MINUTE = 60_000;

interface Item { title: string; color: string | null; dueMs: number; taskId: string; }
interface ArmedBucket { signature: string; timer: ReturnType<typeof setTimeout>; }

/** Live timers keyed by fire-minute. Lets a retitle/reschedule re-arm in place
 *  and same-minute reminders bundle into one notification. Per process. */
const buckets = new Map<number, ArmedBucket>();

/** Notification times follow the APP's 12/24h setting, not the device locale —
 *  same rule the home-screen widgets use. A user who picked 24h in the app must
 *  not get 12h reminders because their phone is set to en-US. */
function fmtTime(ms: number): string {
  return formatHmDate(new Date(ms), useSettingsStore.getState().timeFormat === "12h");
}

function reminderDueMs(t: Task): number {
  const due = new Date(t.due_at!).getTime();
  if (!t.all_day || t.tz) return due;
  // Imported all-day rows without an explicit zone are date-only values. The
  // calendar displays them on the local day, so reminders should fire at local
  // start-of-day rather than at UTC midnight (01:00/02:00 in many zones).
  const d = new Date(t.due_at!);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** One reminder → a plain notification; several in the same minute → a bundle. */
function buildPayload(items: Item[]): NotificationPayload {
  const taskIds = items.map((i) => i.taskId);
  if (items.length === 1) {
    const it = items[0]!;
    return {
      title: it.title || tr("notification.fallbackTitle"),
      body: tr("notification.due", { time: fmtTime(it.dueMs) }),
      color: it.color,
      taskIds,
    };
  }
  return {
    title: tr("notification.bundleTitle", { count: items.length }),
    body: items.map((i) => `• ${i.title || tr("agenda.untitled")}`).join("\n"),
    color: null,
    taskIds,
  };
}

/**
 * Re-arm OS reminders for tasks firing in the next `windowMin`. Idempotent and
 * self-correcting: reminders are grouped into per-minute buckets, each bucket
 * armed once; a bucket whose content changed (retitle, recolor, reschedule) is
 * cancelled and re-armed, and buckets no longer wanted are cancelled. Pass
 * calendar-expanded tasks so recurring occurrences are armed too.
 */
export async function reconcile(tasks: Task[], now: Date, windowMin = 60 * 24, notifier: Notifier = pickNotifier()): Promise<void> {
  const upcoming = upcomingWithin(tasks, now, windowMin);
  const nowBucket = Math.floor(now.getTime() / MINUTE) * MINUTE;

  const desired = new Map<number, Item[]>();
  const pushItem = (bucket: number, item: Item) => {
    const items = desired.get(bucket) ?? [];
    items.push(item);
    desired.set(bucket, items);
  };

  for (const t of upcoming) {
    if (!t.due_at) continue;
    const dueMs = reminderDueMs(t);
    for (const off of t.reminder_offsets) {
      const bucket = Math.floor((dueMs - off * MINUTE) / MINUTE) * MINUTE;
      if (bucket < nowBucket) continue;
      pushItem(bucket, { title: t.title, color: notificationColor(t), dueMs, taskId: t.id });
    }
  }

  // Snoozed tasks/occurrences fire once at their wake time (their due-based
  // offsets are suppressed while snoozed — upcomingWithin already skips non-open
  // statuses). Re-surfacing IS the reminder.
  const nowMs = now.getTime();
  const cutoffMs = nowMs + windowMin * MINUTE;
  for (const t of tasks) {
    if (t.status !== "snoozed" || !t.snoozed_until) continue;
    const wakeMs = new Date(t.snoozed_until).getTime();
    if (wakeMs < nowMs || wakeMs > cutoffMs) continue;
    const bucket = Math.floor(wakeMs / MINUTE) * MINUTE;
    const dueMs = t.due_at ? new Date(t.due_at).getTime() : wakeMs;
    pushItem(bucket, { title: t.title, color: notificationColor(t), dueMs, taskId: t.id });
  }

  // OS-scheduled platforms: hand the whole set over; the adapter diffs against
  // what's already pending so unchanged reminders aren't churned.
  if (notifier.scheduleAll) {
    const fires: ScheduledFire[] = [...desired.entries()]
      .map(([bucket, items]) => {
        items.sort((a, b) => a.title.localeCompare(b.title));
        return { atMs: bucket, payload: buildPayload(items) };
      })
      .sort((a, b) => a.atMs - b.atMs);
    await notifier.scheduleAll(fires);
    return;
  }

  // Drop armed buckets that are no longer wanted (task deleted/completed/moved out).
  for (const [bucket, armed] of buckets) {
    if (!desired.has(bucket)) { clearTimeout(armed.timer); buckets.delete(bucket); }
  }

  // Arm new buckets and re-arm changed ones.
  for (const [bucket, items] of desired) {
    items.sort((a, b) => a.title.localeCompare(b.title));
    const payload = buildPayload(items);
    const signature = JSON.stringify(payload);
    const existing = buckets.get(bucket);
    if (existing && existing.signature === signature) continue;
    if (existing) clearTimeout(existing.timer);
    const delay = Math.min(Math.max(0, bucket - Date.now()), 0x7fffffff);
    // Keep the entry after firing: its signature makes the next reconcile skip
    // re-arming (no duplicate). It's cleared when the task leaves the window or
    // its content changes (which intentionally re-arms with the new content).
    const entry: ArmedBucket = { signature, timer: setTimeout(() => void notifier.send(payload), delay) };
    buckets.set(bucket, entry);
  }
}
